import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader, setCookie, getCookie } from "../Helpers";
import { FiBriefcase, FiArrowRight, FiLock, FiList, FiUser } from "react-icons/fi";
import { MdSearch } from "react-icons/md";
import OneApplication from "../ones/OneApplication"; 

export default function MyApplications() {
  const appContext = useContext(AppContext);

  /* State Management */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  /* Status Category Grouping Hooks */
  const [submittedRecs, setSubmittedRecs] = useState([]);
  const [acceptedRecs, setAcceptedRecs] = useState([]);
  const [rejectedRecs, setRejectedRecs] = useState([]);
  const [hiredRecs, setHiredRecs] = useState([]);
  
  const [allApplications, setAllApplications] = useState([]);
  const [recsList, setRecsList] = useState([]);

  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState('all');

  /* Auth Flow Interceptor States */
  const [isApplying, setIsApplying] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");

  async function init() {
    setReady(false);
    const isAuthenticated = await appContext.authCheck();
    
    if (isAuthenticated) {
      const isProfileValid = await verifyProfileCompleteness();
      if (!isProfileValid) {
        return; // Early breakout avoids flash updates
      }
      await fetchApplications();
    } else {
      setShowEmailPrompt(true);
      setShowOtpPrompt(false);
    }
    setReady(true);
  }

  /* --- PROFILE INTEGRITY VERIFICATION MATRIX --- */
  async function verifyProfileCompleteness() {
    try {
      const response = await callApi('jobapplicant/get-current-applicant-data', {});
      if (response && response.status === 1) {
        const applicant = response.data;
        
        const isIncomplete = 
          !applicant ||
          !applicant.firstName?.trim() ||
          !applicant.middleName?.trim() ||
          !applicant.lastName?.trim() ||
          !applicant.phone?.trim() ||
          !applicant.physicalAddress?.trim() ||
          !applicant.dob?.trim() ||
          !applicant.gender?.trim();

        if (isIncomplete) {
          appContext.tellError("Please fill out your core profile records details completely.");
          appContext.navTo({
            item: 'side-view',
            subItem: 'edit-applicant-profile'
          });
          return false;
        }
        return true;
      } else {
        appContext.tellError(response.msg || "Could not retrieve verification profile files.");
        return false;
      }
    } catch (err) {
      appContext.tellError("Network connection error verification loop.");
      return false;
    }
  }

  async function fetchApplications() {
    await callApi('jobapplicant/get-my-job-applications', {}).then((response) => {
      if (response && response.status === 1) {
        setAllApplications(response.data || []);
        setShowEmailPrompt(false);
        setShowOtpPrompt(false);
      } else {
        appContext.tellError(response.msg || "Could not fetch applications data");
      }
    }).catch(() => {
      appContext.tellError("Network error occurred fetching history.");
    });
  }

  function openProfileEdit() {
    appContext.navTo({
      item: 'side-view',
      subItem: 'edit-applicant-profile'
    });
  }

  /* 💡 Categorize Records Into Split Matrix Closures */
  function categorizeRecs() {
    if (allApplications) {
      const _submitted = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'submitted' || status === 'under-review' || status === 'review' || status === 'pending');
      });

      const _accepted = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'accepted' || status === 'approved');
      });

      const _rejected = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'rejected' || status === 'declined' || status === 'unsuccessful');
      });

      const _hired = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'hired' || status === 'selected' || status === 'employed');
      });

      setSubmittedRecs(_submitted);
      setAcceptedRecs(_accepted);
      setRejectedRecs(_rejected);
      setHiredRecs(_hired);
    }
  }

  /* Process Interactive List Filtering Rules */
  function processRecs() {
    let _list = allApplications;

    if (category === 'submitted') {
      _list = submittedRecs;
    } else if (category === 'accepted') {
      _list = acceptedRecs;
    } else if (category === 'rejected') {
      _list = rejectedRecs;
    } else if (category === 'hired') {
      _list = hiredRecs;
    }

    if (filter && filter.trim().length > 0) {
      const _filtered = _list?.filter(item => {
        return String(item.jobTitle).toLowerCase().indexOf(String(filter).toLowerCase()) > -1;
      });
      setRecsList(_filtered);
    } else {
      setRecsList(_list);
    }
  }

  /* --- VERBATIM FLOW INTERCEPTOR LOGIC --- */
  async function handleEmailSubmit() {
    if (!emailInput || !emailInput.includes('@')) {
      appContext.tellError("Please enter a valid email address.");
      return;
    }
    setIsApplying(true);
    await callApi('jobapplicant/access-account', { email: emailInput }).then(response => {
      if (response.status === 1) {
        appContext.tellMessage(response.msg);
        setShowOtpPrompt(true); 
      } else {
        appContext.tellError(response.msg || "Failed to process request.");
      }
    }).catch(() => appContext.tellError("Network error occurred."));
    setIsApplying(false);
  }

  async function handleOtpSubmit() {
    if (!otpInput) {
      appContext.tellError("Please enter the OTP.");
      return;
    }
    setIsApplying(true);
    await callApi('jobapplicant/verify-otp', { email: emailInput, otp: otpInput }).then(async response => {
      if (response.status === 1) {
        appContext.tellMessage(response.msg);
        setCookie('userUuid', response.data.uuid, 30);
        setCookie('userToken', response.data.token, 30);
        await appContext.authCheck();
        resetPrompts();
        
        const isProfileValid = await verifyProfileCompleteness();
        if (isProfileValid) {
          await fetchApplications();
          setReady(true);
        }
      } else {
        appContext.tellError(response.msg || "Invalid OTP.");
      }
    }).catch(() => appContext.tellError("Network error occurred."));
    setIsApplying(false);
  }

  function resetPrompts() {
    setShowEmailPrompt(false);
    setShowOtpPrompt(false);
    setEmailInput("");
    setOtpInput("");
  }

  useEffect(() => { init(); }, []);
  useEffect(() => { categorizeRecs(); }, [allApplications]);
  useEffect(() => { processRecs(); }, [filter, category, allApplications, submittedRecs, acceptedRecs, rejectedRecs, hiredRecs]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading || isApplying);
    return () => appContext.setShowOverlayLoader(false);
  }, [loading, isApplying]);

  if (ready) {
    return (
      <>
        <style>{`
          :root {
            --clinical-teal: #0d9488;
            --navy-900: #0f172a;
            --slate-50: #f8fafc;
            --slate-100: #f1f5f9;
            --slate-200: #e2e8f0;
            --slate-500: #64748b;
          }
          .apps-panel { 
            font-family: 'Inter', sans-serif; 
            padding: 16px; 
            background: #ffffff; 
            min-height: 100vh; 
          }
          @media (min-width: 576px) {
            .apps-panel { padding: 24px; }
          }
          .jd-email-prompt { display: flex; flex-direction: column; gap: 12px; width: 100%; margin-top: 40px; }
          .jd-email-input { width: 100%; padding: 12px 16px; border: 1px solid var(--slate-200); border-radius: 8px; outline: none; }
          .jd-email-input:focus { border-color: var(--clinical-teal); }
          .jd-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 14px 28px; border-radius: 8px; font-weight: 600; cursor: pointer; border: none; }
          .jd-btn-primary { background: var(--navy-900); color: #ffffff; width: 100%; }
          .jd-btn-primary:hover { background: var(--clinical-teal); }
          
          /* Uniform 4-Column Grid Metric Sizing Handles */
          .summary-card {
            min-height: 85px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          .summary-number { 
            font-size: 20px; 
            font-weight: 700; 
            color: var(--navy-900); 
            line-height: 1.2;
          }
          .summary-label { 
            font-size: 10px; 
            font-weight: 600; 
            color: var(--slate-500);
            text-transform: uppercase;
            letter-spacing: 0.02em;
            margin-top: 2px;
            text-align: center;
          }
          @media (min-width: 576px) {
            .summary-card { min-height: 105px; }
            .summary-number { font-size: 28px; }
            .summary-label { font-size: 11px; }
          }
          
          .profile-shortcut-btn {
            background: var(--slate-100);
            border: 1px solid var(--slate-200);
            color: var(--navy-900);
            border-radius: 30px;
            padding: 6px 12px;
            font-size: 0.75rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
          }
          @media (min-width: 576px) {
            .profile-shortcut-btn {
              padding: 8px 16px;
              font-size: 0.85rem;
              gap: 6px;
            }
          }
          .profile-shortcut-btn:hover {
            background: var(--navy-900);
            color: #ffffff;
            border-color: var(--navy-900);
          }
        `}</style>

        <div className="apps-panel">
          {/* Main Title Row */}
          <div className="border-bottom pb-3 mb-3 d-flex justify-content-between align-items-center gap-2">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <FiBriefcase className="text-muted flex-shrink-0" size={18} />
                <h5 className="fw-bold mb-0 style-color-navy" style={{ color: 'var(--navy-900)' }}>My Applications</h5>
              </div>
              <p className="text-muted small mb-0 d-none d-sm-block">Track real-time status updates for your submitted files.</p>
            </div>

            {!showEmailPrompt && !showOtpPrompt && (
              <button className="profile-shortcut-btn" onClick={openProfileEdit}>
                <FiUser size={12} /> <span className="d-none d-xs-inline">Profile Settings</span><span className="d-xs-none">Profile</span>
              </button>
            )}
          </div>

          {showEmailPrompt || showOtpPrompt ? (
            showOtpPrompt ? (
              <div className="jd-email-prompt">
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(13, 148, 136, 0.1)', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', padding: '16px' }}><FiLock size={24} /></div>
                <div className="text-center my-2 small fw-bold" style={{ color: 'var(--clinical-teal)' }}>We sent a code to {emailInput}</div>
                <input type="text" className="jd-email-input text-center fw-bold" style={{ letterSpacing: '2px' }} placeholder="Enter the 6-digit OTP..." value={otpInput} onChange={(e) => setOtpInput(e.target.value)} autoFocus />
                <div className="d-flex gap-3">
                  <button className="jd-btn jd-btn-primary" onClick={handleOtpSubmit} disabled={isApplying}>Verify OTP <FiArrowRight /></button>
                  <button className="btn btn-danger" onClick={() => appContext.navBack()}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="jd-email-prompt">
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(13, 148, 136, 0.1)', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', padding: '16px' }}><FiLock size={24} /></div>
                <div className="text-center my-2 text-muted small">Please enter your application email address to unlock history logs.</div>
                <input type="email" className="jd-email-input" placeholder="Enter your email to proceed..." value={emailInput} onChange={(e) => setEmailInput(e.target.value)} autoFocus />
                <div className="d-flex gap-3">
                  <button className="jd-btn jd-btn-primary" onClick={handleEmailSubmit} disabled={isApplying}>Continue <FiArrowRight /></button>
                  <button className="btn btn-danger" onClick={() => appContext.navBack()}>Cancel</button>
                </div>
              </div>
            )
          ) : (
            <div className="row g-2 g-sm-3">
              
              {/* Metric Card 1: Submitted */}
              <div className="col-3">
                <div className="card border shadow-sm h-100">
                  <div className="card-body p-1 p-sm-2 summary-card">
                    <div className="summary-number">{submittedRecs?.length || 0}</div>
                    <div className="summary-label">Submitted</div>
                  </div>
                </div>
              </div>

              {/* Metric Card 2: Accepted */}
              <div className="col-3">
                <div className="card border shadow-sm h-100">
                  <div className="card-body p-1 p-sm-2 summary-card">
                    <div className="summary-number">{acceptedRecs?.length || 0}</div>
                    <div className="summary-label">Accepted</div>
                  </div>
                </div>
              </div>

              {/* Metric Card 3: Rejected */}
              <div className="col-3">
                <div className="card border shadow-sm h-100">
                  <div className="card-body p-1 p-sm-2 summary-card">
                    <div className="summary-number">{rejectedRecs?.length || 0}</div>
                    <div className="summary-label">Rejected</div>
                  </div>
                </div>
              </div>

              {/* Metric Card 4: Hired */}
              <div className="col-3">
                <div className="card border shadow-sm h-100">
                  <div className="card-body p-1 p-sm-2 summary-card">
                    <div className="summary-number">{hiredRecs?.length || 0}</div>
                    <div className="summary-label">Hired</div>
                  </div>
                </div>
              </div>

              {/* Filtering Controls */}
              <div className="col-12 my-2 my-sm-3">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="input-group d-none" style={{ width: '60%' }}>
                    <span className="input-group-text bg-light border border-end-0 py-1"><MdSearch size={18}/></span>
                    <input className="form-control border-start-0 py-1 form-control-sm" onChange={(e) => setFilter(e.target.value)} placeholder='Search apps...' />
                  </div>
                  
                  <select className="form-select form-select-sm ms-auto" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: '100%', maxWidth: '150px' }}>
                    <option value='all'>All Applications</option>
                    <option value='submitted'>Submitted</option>
                    <option value='accepted'>Accepted</option>
                    <option value='rejected'>Rejected</option>
                    <option value='hired'>Hired</option>
                  </select>
                </div>
              </div>

              {/* Grid Cards Container */}
              <div className="col-12 mt-1">
                {filter && filter.trim().length > 0 && (
                  <div className="mb-2 text-muted small">
                    Search Results For: <span className="fw-bold">{filter}</span>
                  </div>
                )}

                <div className="row row-cols-1 g-2">
                  {recsList && recsList.length > 0 ? (
                    recsList.map((item, i) => (
                      <div className="col" key={item.uuid || i}>
                        <OneApplication data={item} />
                      </div>
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="text-center py-5 text-muted bg-light rounded-3 border">
                        <FiList size={28} className="mb-2" style={{ opacity: 0.5 }} />
                        <p className="small mb-0">No applications found in this category</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  } else {
    return (
      <div className="container py-5 text-center">
        {getInlineLoader()}
      </div>
    );
  }
}